import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress, Skeleton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CloseIcon from '@mui/icons-material/Close';
import GenericButton from '../shared/Button';
import {
  getUserWallets,
  decodeJWT,
  baseURL,
  getUserInvestments,
  getUserDemoWallets,
  getDemoUserInvestments,
  getUserShortToken,
  getUserDetails,
} from '../../../services/api';
import { getKycStatus } from '../../../services/kycApi';
import { getKybStatus } from '../../../services/kybApi';
import { useNavigate, useSearchParams } from 'react-router-dom';

// Define the makeStyles hook
const useStyles = makeStyles((theme) => ({
  container: {
    // maxWidth: '1380px',
    width: '100%',
    padding: '24px',
    borderRadius: '16px',
    display: 'flex',
    flexDirection: 'column',
    '&>div': {
      display: 'flex',
      [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
      },
    },
    margin: '0 auto',
    border: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      gap: '15px',
      border: 'none !important',
    },
  },
  balanceSection: {
    display: 'grid',
    gridTemplateColumns: 'auto auto',
    flex: 2,
    gap: '50px',
    [theme.breakpoints.down('md')]: {
      gridTemplateColumns: 'auto',
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    '& h6': {
      fontSize: '20px !important',
      color: `${theme.palette.text.primary} !important`,
      fontWeight: '600',
    },
  },
  hiddenBalance: {
    fontSize: '32px !important',
    marginTop: '8px',
    fontWeight: '600 !important',
  },
  pnlTextToday: {
    fontSize: '14px !important',
    marginTop: '35px !important',
  },
  pnlText: {
    fontSize: '14px !important',
    marginTop: '15px !important',
  },
  eyeIcon: {
    marginLeft: '8px',
    cursor: 'pointer',
  },
  redText: {
    color: 'red !important',
  },
  greenText: {
    color: `${theme.palette.primary.main} !important`,
  },
  buttonContainer: {
    display: 'flex',
    flex: 1,
    gap: '10px',
    [theme.breakpoints.down('md')]: {
      flex: 1,
      marginTop: '20px',
    },
  },
  button: {
    fontSize: '13px !important',
    lineHeight: '10px !important',
    padding: '0px 12px !important',
    height: '28px !important',
    background: `${theme.palette.divider} !important`,
    color: `${theme.palette.text.primary} !important`,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
  },
  verificationPopup: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1200,
  },
  popupContent: {
    backgroundColor: theme.palette.mode === 'light' ? '#ffffff' : '#1e2329',
    borderRadius: '16px',
    padding: '24px',
    paddingTop: '32px',
    maxWidth: '400px',
    width: '90%',
    textAlign: 'center',
    position: 'relative',
    '& h3': {
      color: `${theme.palette.text.primary} !important`,
      fontSize: '20px',
      fontWeight: 600,
      marginBottom: '15px',
      paddingRight: '32px',
    },
    '& p': {
      color: `${theme.palette.text.primary} !important`,
      fontSize: '14px',
      marginBottom: '20px',
    },
  },
  closeButton: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    cursor: 'pointer',
    color: theme.palette.text.secondary,
    zIndex: 1,
    '&:hover': {
      color: theme.palette.text.primary,
    },
  },
  popupButtons: {
    display: 'flex',
    gap: '10px',
    justifyContent: 'center',
  },
  verificationButton: {
    fontSize: '14px !important',
    padding: '10px 24px !important',
    height: 'auto !important',
    background: 'var(--primary-color, #11BE6A) !important',
    color: '#fff !important',
    fontWeight: '600 !important',
    borderRadius: '8px !important',
    '&:hover': {
      opacity: '0.9 !important',
    },
  },
}));

const SPECIAL_WITHDRAW_EMAIL = 'fatham.llc@gmail.com';
const WITHDRAW_EXCEPTION_LIMIT = 600;

const BalanceOverview = ({
  selectedValue,
  currentPath = '/wallet/overview',
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [visible, setVisible] = useState(false);
  const [visibleStaking, setVisibleStaking] = useState(false);
  const [pnl, setPnl] = useState({ value: 0, percentage: 0 });
  const [pnlClass, setPnlClass] = useState(classes.redText);
  const [profitClass, setProfitClass] = useState(classes.redText);
  const [investmentStatus, setInvestmentStatus] = useState(classes.greenText);
  const [totalBalanceInUSD, setTotalBalanceInUSD] = useState(0);
  const [totalStakedBalanceInUSD, setTotalStakedBalanceInUSD] = useState(0); // State for staked balance
  const [totalInvestment, setTotalInvestment] = useState(0); // State for staked balance
  const [isLoading, setIsLoading] = useState(true);
  const [visibleTotalBalance, setVisibleTotalBalance] = useState(false);
  const [visibleTotalInvestment, setVisibleTotalInvestment] = useState(false);
  const [profit, setProfit] = useState({ value: 0, percentage: 0 });
  const [email, setEmail] = useState('');
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);
  const [showTransferVerificationPopup, setShowTransferVerificationPopup] = useState(false);
  const [showWithdrawNotice, setShowWithdrawNotice] = useState(false);
  const [checkingVerification, setCheckingVerification] = useState(false);
  const [checkingTransferVerification, setCheckingTransferVerification] = useState(false);
  const [userType, setUserType] = useState(null);
  const handleToggleVisibility = () => {
    setVisible(!visible);
  };

  const handleToggleStakingVisibility = () => {
    setVisibleStaking(!visibleStaking);
  };
  const handleToggleTotalBalanceVisibility = () => {
    setVisibleTotalBalance(!visibleTotalBalance);
  };
  const handleToggleTotalInvestmentVisibility = () => {
    setVisibleTotalInvestment(!visibleTotalInvestment);
  };

  // Check if advanced verification (KYC or KYB) is completed
  const checkAdvancedVerification = async () => {
    try {
      setCheckingVerification(true);
      
      // Get user details to check isKYCPass
      let userEmail = email || String(localStorage.getItem('email'));
      if (!userEmail) {
        const token = localStorage.getItem('access_token');
        if (token) {
          const decoded = decodeJWT(token);
          userEmail = decoded.email;
        }
      }

      if (!userEmail) {
        return false;
      }

      const userDetailsRes = await getUserDetails(userEmail);
      const userData = userDetailsRes.data;
      const isKycPass = userData?.isKYCPass;

      // Check KYC status
      let kycApproved = false;
      try {
        const kycStatusRes = await getKycStatus();
        const kycStatus = kycStatusRes?.data?.data || kycStatusRes?.data;
        kycApproved = kycStatus?.status === 'approved' || isKycPass;
      } catch (kycError) {
        // KYC status might not exist - that's okay
        kycApproved = isKycPass;
      }

      // Check KYB status
      let kybApproved = false;
      try {
        const kybStatusRes = await getKybStatus();
        const kybStatus = kybStatusRes?.data?.data || kybStatusRes?.data;
        kybApproved = kybStatus?.application?.status === 'approved';
      } catch (kybError) {
        // KYB status might not exist - that's okay
        kybApproved = false;
      }

      // Advanced verification is complete if either KYC or KYB is approved
      return kycApproved || kybApproved;
    } catch (error) {
      console.error('Error checking verification status:', error);
      return false;
    } finally {
      setCheckingVerification(false);
    }
  };

  // Handle withdraw button click
  const handleWithdrawClick = async () => {
    const isVerified = await checkAdvancedVerification();
    
    if (!isVerified) {
      const currentEmail = String(
        email || localStorage.getItem('email') || ''
      ).toLowerCase();
      const eligibleBalance = Number(totalBalanceInUSD) < WITHDRAW_EXCEPTION_LIMIT;

      if (
        currentEmail === SPECIAL_WITHDRAW_EMAIL &&
        eligibleBalance
      ) {
        setShowWithdrawNotice(true);
        return;
      }

      // Show popup asking user to complete verification
      setShowVerificationPopup(true);
      return;
    }

    // Allow withdrawal
    navigate('/withdraw-crypto-select-coin');
  };

  // Handle navigate to account settings
  const handleCompleteVerification = () => {
    setShowVerificationPopup(false);
    navigate('/indexx-exchange/account');
  };

  const handleContinueWithdraw = () => {
    setShowWithdrawNotice(false);
    navigate('/withdraw-crypto-select-coin');
  };

  // Handle transfer button click with KYC check
  const handleTransferClick = async () => {
    setCheckingTransferVerification(true);
    const isVerified = await checkAdvancedVerification();

    if (!isVerified) {
      setShowTransferVerificationPopup(true);
      return;
    }

    navigate('/indexx-exchange/send');
  };

  useEffect(() => {
    // Get userType from localStorage and apply theme
    const currentUserType = localStorage.getItem('userType');
    const userLogged = localStorage.getItem('userlogged');
    setUserType(currentUserType);
    
    // Apply theme class based on userType
    let themeClass = 'theme-normal'; // Default to green
    if (currentUserType && currentUserType !== 'Indexx Exchange') {
      themeClass = 'theme-captain'; // Yellow for CaptainBee
    } else if (currentUserType === 'CaptainBee' || userLogged === 'captain') {
      themeClass = 'theme-captain'; // Yellow for CaptainBee
    } else if (currentUserType === 'HoneyUser' || userLogged === 'honeyb') {
      themeClass = 'theme-honeyb'; // Yellow for HoneyBee
    } else if (userLogged === 'normal' || currentUserType === 'Indexx Exchange') {
      themeClass = 'theme-normal'; // Green for normal users
    }
    
    // Apply theme class to document
    document.body.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
    document.body.classList.add(themeClass);
    document.documentElement.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
    document.documentElement.classList.add(themeClass);
    
    const root = document.getElementById('root');
    if (root) {
      root.classList.remove('theme-normal', 'theme-captain', 'theme-honeyb');
      root.classList.add(themeClass);
    }
    
    const fetchUserWallets = async () => {
      setIsLoading(true);
      try {
        let email = String(localStorage.getItem('email'));
        setEmail(email);
        if (!email) {
          navigate('/auth/login');
          return;
        }
        let getInvestments, userWallets;
        let userCurrentPath = window.location.pathname;
        if (userCurrentPath === '/wallet/demo-smart-crypto') {
          getInvestments = await getDemoUserInvestments(email);
          userWallets = await getUserDemoWallets(email);
        } else {
          getInvestments = await getUserInvestments(email);
          userWallets = await getUserWallets(email);
        }
        const usersWallet = userWallets.data;
        let totalBalInUSD = 0;
        let totalPrevBalInUSD = 0;
        let totalStakedBalInUSD = 0; // Variable for staked balance

        let totalInvestment = getInvestments.data; // Variable for staked balance
        setTotalInvestment(totalInvestment);
        const processedSymbols = new Set(); // To track already processed INEX and WIBS

        usersWallet.forEach((wallet) => {
          const balance = Number(wallet.coinBalance);
          const stakedBalance = Number(wallet.coinStakedBalance); // Assuming stakingBalance is available in the API response
          const price = Number(wallet.coinPrice);
          const prevPrice = Number(wallet.coinPrevPrice);
          //debugger;
          // Specific handling for INEX and WIBS to avoid duplicates
          if (wallet.coinSymbol === 'INEX' || wallet.coinSymbol === 'WIBS') {
            if (processedSymbols.has(wallet.coinSymbol)) {
              return; // Skip if already processed
            }
            processedSymbols.add(wallet.coinSymbol); // Mark as processed

            if (balance > 0) {
              if (!isNaN(price)) {
                totalBalInUSD += balance * price;
              } else {
                totalBalInUSD += balance;
              }
            }

            if (stakedBalance > 0 && !isNaN(price)) {
              totalStakedBalInUSD += stakedBalance * price;
            }
          } else {
            // General logic for other symbols
            if (balance > 0 && wallet.coinSymbol !== 'USD') {
              if (
                wallet.coinType === 'Crypto' &&
                !isNaN(price) &&
                !isNaN(prevPrice)
              ) {
                totalBalInUSD += balance * price;
                totalPrevBalInUSD += balance * prevPrice;
              } else if (!isNaN(price)) {
                totalBalInUSD += balance * price;
              } else {
                totalBalInUSD += balance;
              }
            }

            if (wallet.coinSymbol === 'USD' && wallet.coinBalance > 0) {
              totalBalInUSD += wallet.coinBalance;
            }

            if (stakedBalance > 0 && !isNaN(price)) {
              totalStakedBalInUSD += stakedBalance * price;
            }
          }
        });

        setTotalBalanceInUSD(totalBalInUSD);
        setTotalStakedBalanceInUSD(totalStakedBalInUSD); // Set staked balance

        if (email === 'joel@gearboxsports.com') {
          setTotalStakedBalanceInUSD(1000); // Set staked balance
        }
        let pnlValue = 0;
        let pnlPercentage = 0;

        console.log('Final totalBalanceInUSD', totalBalInUSD);
        console.log('Final totalPrevBalInUSD', totalPrevBalInUSD);
        if (totalPrevBalInUSD > 0) {
          pnlValue = totalBalInUSD - totalPrevBalInUSD;
          console.log(pnlValue);
          pnlPercentage = (pnlValue / totalPrevBalInUSD) * 100;
        }

        setPnl({
          value: pnlValue.toFixed(2),
          percentage: pnlPercentage.toFixed(2),
        });
        // Set the class based on PNL value
        setPnlClass(pnlValue >= 0 ? classes.greenText : classes.redText);

        let adjustedBonus = 0;
        if (
          totalBalInUSD > 0 &&
          ['paul@diversifiedlandscape.com', 'bmoralez12@gmail.com'].includes(
            email
          )
        ) {
          adjustedBonus = 3000;
        }

        const totalBalance =
          totalBalInUSD + totalStakedBalInUSD + adjustedBonus;
        const profitValue = totalBalance - totalInvestment;

        const profitPercentage =
          totalInvestment > 0 ? (profitValue / totalInvestment) * 100 : 0;

        setProfit({
          value: profitValue.toFixed(2),
          percentage: profitPercentage.toFixed(2),
        });
        // Dynamically set the profit class
        setProfitClass(profitValue >= 0 ? classes.greenText : classes.redText);
      } catch (err) {
      } finally {
        setIsLoading(false);
      }
    };
    fetchUserWallets();
  }, [navigate, searchParams]);

  return (
    <Box className={classes.container}>
      <Box>
        <Box className={classes.balanceSection}>
          <Box className={classes.balanceSectionWrapper}>
            <Box className={classes.header}>
              <Typography variant="h6">
                {email === 'wallet@azooca.com'
                  ? '10% Balance'
                  : 'Estimated Balance'}
              </Typography>
              <div
                className={classes.eyeIcon}
                onClick={handleToggleVisibility}
                size="small"
                style={{ cursor: 'pointer' }}
              >
                {visible ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </div>
            </Box>
            <Typography className={classes.hiddenBalance}>
              {isLoading ? (
                <Skeleton width="100px" height={40} />
              ) : (
                `$${
                  visible
                    ? '*******'
                    : `${new Intl.NumberFormat('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(
                        totalBalanceInUSD +
                          (totalBalanceInUSD > 0 &&
                          [
                            'paul@diversifiedlandscape.com',
                            'bmoralez12@gmail.com',
                          ].includes(email)
                            ? 3000
                            : 0)
                      )}`
                }`
              )}
            </Typography>
          </Box>
          <Box className={classes.balanceSectionWrapper}>
            <Box className={classes.header}>
              <Typography variant="h6">
                {email === 'wallet@azooca.com'
                  ? '3% Balance'
                  : 'Staked Balance'}
              </Typography>
              <div
                className={classes.eyeIcon}
                onClick={handleToggleStakingVisibility}
                size="small"
                style={{ cursor: 'pointer' }}
              >
                {visibleStaking ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </div>
            </Box>
            <Typography className={classes.hiddenBalance}>
              {isLoading ? (
                <Skeleton width="100px" height={40} />
              ) : (
                `$${
                  visibleStaking
                    ? '*******'
                    : `${new Intl.NumberFormat('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(totalStakedBalanceInUSD)}`
                }
                `
              )}
            </Typography>
          </Box>
          <Box className={classes.balanceSectionWrapper}>
            <Box className={classes.header}>
              <Typography variant="h6">Total Balance</Typography>
              <div
                className={classes.eyeIcon}
                onClick={handleToggleTotalBalanceVisibility}
                size="small"
                style={{ cursor: 'pointer' }}
              >
                {visibleTotalBalance ? (
                  <VisibilityOffIcon />
                ) : (
                  <VisibilityIcon />
                )}
              </div>
            </Box>
            <Typography className={classes.hiddenBalance}>
              {isLoading ? (
                <Skeleton width="100px" height={40} />
              ) : (
                `$${
                  visibleTotalBalance
                    ? '*******'
                    : `${new Intl.NumberFormat('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(
                        totalBalanceInUSD +
                          totalStakedBalanceInUSD +
                          (totalBalanceInUSD > 0 &&
                          [
                            'paul@diversifiedlandscape.com',
                            'bmoralez12@gmail.com',
                          ].includes(email)
                            ? 3000
                            : 0)
                      )}`
                }`
              )}
            </Typography>
          </Box>
          <Box className={classes.balanceSectionWrapper}>
            <Box className={classes.header}>
              <Typography variant="h6">Investment Amount</Typography>
              <div
                className={classes.eyeIcon}
                onClick={handleToggleTotalInvestmentVisibility}
                size="small"
                style={{ cursor: 'pointer' }}
              >
                {visibleTotalInvestment ? (
                  <VisibilityOffIcon />
                ) : (
                  <VisibilityIcon />
                )}
              </div>
            </Box>
            <Typography className={classes.hiddenBalance}>
              {isLoading ? (
                <Skeleton width="100px" height={40} />
              ) : (
                `$${
                  visibleTotalInvestment
                    ? '*******'
                    : `${new Intl.NumberFormat('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(totalInvestment)}`
                }
                `
              )}
            </Typography>
          </Box>
        </Box>
        {currentPath !== '/wallet/demo-smart-crypto' && (
          <Box className={classes.buttonContainer}>
            <GenericButton
              text={'Deposit'}
              className={classes.button}
              onClick={() => navigate('/deposit-crypto-select-coin')}
            />
            <GenericButton
              text={checkingVerification ? 'Checking...' : 'Withdraw'}
              className={classes.button}
              onClick={handleWithdrawClick}
              disabled={checkingVerification}
            />
            <GenericButton
              text={checkingTransferVerification ? 'Checking...' : 'Transfer'}
              className={classes.button}
              onClick={handleTransferClick}
              disabled={checkingTransferVerification}
            />
          </Box>
        )}
      </Box>

      <Typography className={classes.pnlTextToday}>
        Today's PNL:{' '}
        <span className={pnlClass}>{`$${pnl.value} (${pnl.percentage}%)`}</span>
      </Typography>

      {/* Profit Section */}
      <Typography className={classes.pnlText}>
        Portfolio PNL:{' '}
        <span className={profitClass}>
          {`$${profit.value} (${profit.percentage}%)`}
        </span>
      </Typography>

      {(email === 'kariej.smith@yahoo.com') && (
        <Typography className={classes.pnlText}>
          Smart APY Investment Status:{' '}
          <span className={investmentStatus}>{`Deposit Completed`}</span>
        </Typography>
      )}

      {/* Verification Popup */}
      {showVerificationPopup && (
        <div className={classes.verificationPopup}>
          <div className={classes.popupContent}>
            <CloseIcon 
              className={classes.closeButton} 
              onClick={() => setShowVerificationPopup(false)} 
            />
            <h3>Complete Advanced Verification</h3>
            <p>
              Please complete your advanced verification (KYC or KYB) to process withdrawals. 
              This helps us ensure the security of your account and comply with regulatory requirements.
            </p>
            <div className={classes.popupButtons}>
              <GenericButton
                text="Complete Verification"
                className={classes.verificationButton}
                onClick={handleCompleteVerification}
              />
            </div>
          </div>
        </div>
      )}

      {showTransferVerificationPopup && (
        <div className={classes.verificationPopup}>
          <div className={classes.popupContent}>
            <CloseIcon
              className={classes.closeButton}
              onClick={() => setShowTransferVerificationPopup(false)}
            />
            <h3>Complete Advanced Verification</h3>
            <p>
              Please complete your advanced verification (KYC or KYB) to transfer funds.
              This helps us ensure the security of your account and comply with regulatory requirements.
            </p>
            <div className={classes.popupButtons}>
              <GenericButton
                text="Complete Verification"
                className={classes.verificationButton}
                onClick={() => {
                  setShowTransferVerificationPopup(false);
                  navigate('/indexx-exchange/account');
                }}
              />
            </div>
          </div>
        </div>
      )}

      {showWithdrawNotice && (
        <div className={classes.verificationPopup}>
          <div className={classes.popupContent}>
            <CloseIcon 
              className={classes.closeButton} 
              onClick={() => setShowWithdrawNotice(false)} 
            />
            <h3>Withdrawal Under $600</h3>
            <p>
              You can withdraw under $600 without completing KYC or KYB. Please
              complete verification for future withdrawals.
            </p>
            <div className={classes.popupButtons}>
              <GenericButton
                text="Continue to Withdraw"
                className={classes.verificationButton}
                onClick={handleContinueWithdraw}
              />
            </div>
          </div>
        </div>
      )}
    </Box>
  );
};

export default BalanceOverview;
